import { api as wiredApi } from './index';

/**
 * A client booking a delivery on InGo's API (the mobile plan's M4 and M5):
 * the services the tariff prices, places from InGo's own map server, the
 * price quoted on the road and held for a quarter of an hour, the booking at
 * that price, and the delivery followed to the door. The client is the
 * token: no id is ever sent for them.
 *
 * The price is the server's. The old screens worked it out in the phone and
 * let the client raise it; a booking now sends the quote it was given, and a
 * quote that no longer holds is refused (`QUOTE_EXPIRED`, `PRICE_CHANGED`,
 * `PINS_CHANGED`), never re-priced silently.
 */
export function createBooking({ api }) {
  return {
    /** `GET quotes/services`: what can be booked today, `[{key, label}]`. */
    async services() {
      const { data } = await api.get('/quotes/services');

      return Array.isArray(data?.services) ? data.services : [];
    },

    /**
     * `GET places/search`: places inside the service area, each with its pin.
     * `near` ({ lat, lng }): places near it first (InGo ADR 0054), sent to
     * about a kilometre, the closest the server ranks by. `typeAhead`: a
     * suggestion that comes late is no use, so it gives up after 8 s and is
     * never retried; the next key asks again.
     */
    async searchPlaces(text, { near = null, typeAhead = false } = {}) {
      const at = near && Number.isFinite(near.lat) && Number.isFinite(near.lng) ? near : null;
      const { data } = await api.get('/places/search', {
        query: { q: text, latitude: at ? Number(at.lat.toFixed(2)) : undefined, longitude: at ? Number(at.lng.toFixed(2)) : undefined },
        ...(typeAhead ? { timeoutMs: 8000, retry: false } : {}),
      });

      return Array.isArray(data?.places) ? data.places : [];
    },

    /**
     * `GET places/reverse`: the place at a point, or null, and whether InGo
     * delivers there (`served`; InGo ADR 0055: named wherever the maps
     * reach). A server before that answers no `served`: read as served, as it
     * refused the rest. `timeoutMs`: one try that gives up early, never
     * retried here; the caller tries again.
     */
    async reversePlace(latitude, longitude, { timeoutMs } = {}) {
      const { data } = await api.get('/places/reverse', {
        query: { latitude, longitude },
        ...(timeoutMs ? { timeoutMs, retry: false } : {}),
      });

      return { place: data?.place ?? null, served: data?.served !== false };
    },

    /** `POST quotes`: the price from pickup to drop-off, on the road. */
    async quote(serviceType, pickup, dropoff) {
      const { data } = await api.post('/quotes', {
        service_type: serviceType,
        points: [
          { latitude: pickup.latitude, longitude: pickup.longitude },
          { latitude: dropoff.latitude, longitude: dropoff.longitude },
        ],
      });

      return data.quote;
    },

    /**
     * `POST customer/me/deliveries`: booked at the quote's price, paid the way
     * the client chose (cash unless said). `pickup` and `dropoff` are the pins
     * the quote was priced on. Paid online, it waits for its money before a
     * rider is asked (InGo ADR 0047).
     */
    async book({ quote, pickup, dropoff, parcel, paymentMethod = 'cash' }) {
      const stop = (pin) => ({
        address: pin.address,
        latitude: pin.latitude,
        longitude: pin.longitude,
        ...(pin.contactName ? { contact_name: pin.contactName } : {}),
        ...(pin.contactPhone ? { contact_phone: pin.contactPhone } : {}),
      });
      const { data } = await api.post('/customer/me/deliveries', {
        quote_id: quote.id,
        total_minor: quote.total_minor,
        payment_method: paymentMethod,
        pickup: stop(pickup),
        dropoff: stop(dropoff),
        package: {
          size: parcel.size,
          category: parcel.category,
          ...(parcel.category === 'other' && parcel.categoryOther ? { category_other: parcel.categoryOther } : {}),
          ...(parcel.weightKg != null && parcel.weightKg !== '' ? { weight_kg: Number(parcel.weightKg) } : {}),
          ...(parcel.notes ? { notes: parcel.notes } : {}),
        },
      });

      return data;
    },

    /** `GET customer/me/deliveries`: the client's own, newest first (one page). */
    async deliveries() {
      const { data } = await api.get('/customer/me/deliveries');

      return Array.isArray(data) ? data : [];
    },

    /** `GET customer/me/deliveries/{id}`: only the client's own. */
    async delivery(id) {
      const { data } = await api.get(`/customer/me/deliveries/${id}`);

      return data;
    },

    /**
     * The client's own photo of the parcel (Rio, 11 Oct 2026), which their
     * rider sees once they take the job; replaced until a rider collects it.
     */
    async attachPackagePhoto(id, photo, fileName = 'parcel.jpg') {
      const form = new FormData();
      form.append('photo', photo, fileName);

      const { data } = await api.post(`/customer/me/deliveries/${id}/package-photo`, undefined, { form });

      return data;
    },

    /** The client's own photo of the parcel, as they booked it: a Blob. */
    async packagePhoto(id) {
      return api.blob(`/customer/me/deliveries/${id}/package-photo`);
    },

    /** The rider's photo of the parcel when they collected it: a Blob. */
    async pickupPhoto(id) {
      return api.blob(`/customer/me/deliveries/${id}/pickup-photo`);
    },

    /** The photo a rider took of a problem with the parcel: a Blob. */
    async problemPhoto(id, problemId) {
      return api.blob(`/customer/me/deliveries/${id}/problems/${problemId}/photo`);
    },

    /** `POST customer/me/deliveries/{id}/cancellation`: until a rider has it. */
    async cancel(id, reason = null) {
      const { data } = await api.post(`/customer/me/deliveries/${id}/cancellation`, reason ? { reason } : {});

      return data;
    },
  };
}

export const booking = createBooking({ api: wiredApi });

/** The client's parcel sizes and kinds, their app's words (`PackageDetailsPage.js`), as InGo names them. */
export const PARCEL_SIZES = { Small: 'small', Medium: 'medium', Large: 'large', 'Extra Large': 'xlarge', 'Extra large': 'xlarge' };
export const PARCEL_CATEGORIES = { Documents: 'documents', Electronics: 'electronics', Clothing: 'clothing', Food: 'food', Fragile: 'fragile', Other: 'other' };
